import { useState, type ReactNode } from "react";
import { ChevronDown, FolderOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ProjectPickerList } from "@/components/building/ProjectPickerList";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import type { TemplateFormTranslations } from "@/lib/i18n/types/documents-sections/templateForm";
import { useProjects } from "../../hooks/useProjects";
import type { SavedTemplateForm } from "../../hooks/useSavedTemplateForms";

export interface SavedFormsState {
  list: SavedTemplateForm[];
  /** `noProject` — the form has no project scope (building view), so there is
   *  no list to fetch: saved forms live in a project, and this document's is
   *  only chosen when saving. Distinct from `ready`-and-empty, which would
   *  read as "this project has no saved forms". */
  status: "loading" | "error" | "ready" | "noProject";
  retry: () => void;
}

/**
 * Lets the picker ask which project's filed forms to draw on. Passed only at
 * building scope, where the page has no project of its own — everywhere else
 * the answer is already known and asking would be noise.
 */
export interface SavedFormsProjectChoice {
  buildingId: string;
  projectId: string | null;
  onProjectChange: (projectId: string) => void;
}

interface SavedFormPickerProps {
  copy: TemplateFormTranslations;
  forms: SavedFormsState;
  /** The button that opens the popover — the two callers look different. */
  trigger: ReactNode;
  searchPlaceholder: string;
  emptyLabel: string;
  onPick: (form: SavedTemplateForm) => void;
  projectChoice?: SavedFormsProjectChoice;
}

/**
 * Picks one of the project's already-filed forms. Shared by "Choose pre-filled
 * form", which copies the whole form, and by a field's Import, which takes only
 * that field — the choosing is the same act either way.
 */
export function SavedFormPicker({
  copy,
  forms,
  trigger,
  searchPlaceholder,
  emptyLabel,
  onPick,
  projectChoice,
}: SavedFormPickerProps) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        {projectChoice && (
          <div className="space-y-1 border-b p-2">
            <Label className="px-2 text-xs text-muted-foreground">{copy.savedFormsProject}</Label>
            <SourceProjectSelect copy={copy} choice={projectChoice} />
          </div>
        )}
        {forms.status === "noProject" ? (
          // Two different truths. Given the dropdown above, the answer is one
          // click away and the copy asks for it; without it there is nothing
          // the user can do here and the copy says why instead.
          <p className="p-4 text-sm text-muted-foreground">
            {projectChoice ? copy.savedFormsPickProject : copy.savedFormsNeedProject}
          </p>
        ) : (
          <Command>
            <CommandInput placeholder={searchPlaceholder} />
            <CommandList>
              <CommandEmpty>
                {forms.status === "loading" ? copy.loadingDocuments : emptyLabel}
              </CommandEmpty>
              {forms.status === "ready" && (
                <CommandGroup>
                  {forms.list.map((form) => (
                    <CommandItem
                      key={form.id}
                      // The id keeps the value unique — cmdk resolves the
                      // highlight by value, and two forms can share a name.
                      value={`${form.documentName} ${form.id}`}
                      onSelect={() => {
                        onPick(form);
                        setOpen(false);
                      }}
                    >
                      <span className="truncate">{form.documentName}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        )}
      </PopoverContent>
    </Popover>
  );
}

/**
 * The building's projects, using the same `ProjectPickerList` body as the
 * sidebar so switching source project here feels consistent.
 *
 * Unlike the sidebar picker, this does not write the `?project=` search param
 * or global scope store. It chooses where to read filed forms from and nothing
 * else.
 */
function SourceProjectSelect({
  copy,
  choice,
}: {
  copy: TemplateFormTranslations;
  choice: SavedFormsProjectChoice;
}) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  // Already excludes hidden and archived projects, which is the same list the
  // header's selector shows.
  const { projects } = useProjects(choice.buildingId);
  const selected = projects.find((project) => project.id === choice.projectId);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          role="combobox"
          aria-expanded={open}
          aria-label={copy.savedFormsProject}
          className="h-9 w-full justify-start gap-1.5 px-2 hover:bg-transparent"
        >
          {selected ? (
            <>
              <FolderOpen className="h-4 w-4 shrink-0 text-primary" />
              <span className="truncate font-medium text-primary">{selected.name}</span>
            </>
          ) : (
            <span className="truncate font-medium text-muted-foreground">
              {t.buildings.scope.selectProject}
            </span>
          )}
          <ChevronDown className="ml-auto h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      {/* Above the z-[60] the picker dialogs use, so it is not painted under
          the dialog it belongs to — the same reason theirs were raised. */}
      <PopoverContent className="z-[70] w-[320px] p-0" align="start">
        <ProjectPickerList
          projects={projects.map((project) => ({
            id: Number(project.id),
            name: project.name,
            status: project.status,
          }))}
          selectedProjectId={choice.projectId === null ? null : Number(choice.projectId)}
          onSelect={(projectId) => {
            choice.onProjectChange(String(projectId));
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
